import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type InfiniteData,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { listNfts, syncNfts, getNftById, type ListNftsResponse } from "../../../shared/api/nft";
import type { Nft } from "../../../entities/nft";
import { mapNftResponseToEntity } from "../../../entities/nft";

export const nftsKeys = {
  base: ["nfts"] as const,
  list: (walletId?: string) => ["nfts", walletId ?? "all"] as const,
  detail: (nftId: string) => ["nfts", "detail", nftId] as const,
};

type UseNftsInfiniteQueryParams = {
  walletId?: string;
  limit?: number;
};

export const useNftsInfiniteQuery = (
  params?: UseNftsInfiniteQueryParams,
  options?: Omit<UseInfiniteQueryOptions<{ items: Nft[]; nextCursor?: string }, Error, InfiniteData<{ items: Nft[]; nextCursor?: string }, string | undefined>, ReturnType<typeof nftsKeys.list>, string | undefined>, 'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'>
) =>
  useInfiniteQuery<
    { items: Nft[]; nextCursor?: string },
    Error,
    InfiniteData<{ items: Nft[]; nextCursor?: string }, string | undefined>,
    ReturnType<typeof nftsKeys.list>,
    string | undefined
  >({
    queryKey: nftsKeys.list(params?.walletId),
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const response = await listNfts({
        walletId: params?.walletId,
        limit: params?.limit,
        cursor: pageParam ?? undefined,
      });
      return {
        items: response.items.map(mapNftResponseToEntity),
        nextCursor: response.nextCursor,
      };
    },
    getNextPageParam: lastPage => lastPage?.nextCursor ?? undefined,
    ...options,
  });

export const useNftByIdQuery = (
  nftId: string,
  options?: Omit<UseQueryOptions<Nft, Error>, 'queryKey' | 'queryFn'>
) =>
  useQuery<Nft, Error>({
    queryKey: nftsKeys.detail(nftId),
    queryFn: async () => {
      const response = await getNftById(nftId);
      return mapNftResponseToEntity(response);
    },
    ...options,
  });

export const useSyncNftsMutation = (): UseMutationResult<
  { status: string; scope: "user" | "wallet"; walletId?: string | undefined },
  Error,
  string | undefined
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (walletId?: string) => syncNfts(walletId ? { walletId } : undefined),
    onSuccess: (_result, walletId) => {
      queryClient.invalidateQueries({ queryKey: nftsKeys.list(undefined) });
      if (walletId) {
        queryClient.invalidateQueries({ queryKey: nftsKeys.list(walletId) });
      }
    },
  });
};
